<script lang="ts">
  // Full NPC bust (the dialog / duel-board portrait), a tight head crop
  // (`headshot`), or a "window" portrait (`windowed`, the full bust framed inside
  // a square, head + upper torso, clipped). Renders the character's 3×3 paper-doll
  // tiles as absolutely-positioned <img>s, the same cells the in-world billboard
  // composites in Pixi, but as plain DOM so it drops straight into Svelte with no
  // extra canvas/app. The full view is cropped horizontally to the columns the
  // figure occupies (figures sit left/centre of the 768 canvas, not filling it).
  import type { NpcBust } from "@ygoevo/yo2-data";
  import { asset } from "../../data.ts";

  const CANVAS = 768;
  const CELL = 256;
  // Head region within the 768 canvas (matches PortraitPreview so player + NPC
  // crops frame alike): vertical centre + the slice height that fills the box.
  const HEAD_CY = 150;
  const HEAD_H = 360;
  // "Window" framing, same tuning as PortraitPreview (head dominates, a little chest).
  const WIN_CY = 165;
  const WIN_H = 350;

  let { bust, height = 300, headshot = false, windowed = false }: { bust: NpcBust; height?: number; headshot?: boolean; windowed?: boolean } = $props();

  // A square crop (headshot or window) frames the figure inside `height`×`height`.
  const square = $derived(headshot || windowed);
  const cy = $derived(windowed ? WIN_CY : HEAD_CY);
  const sliceH = $derived(windowed ? WIN_H : HEAD_H);

  // Columns the figure occupies → the full view crops horizontally to them.
  const minCol = $derived(bust.cells.length ? Math.min(...bust.cells.map((c) => c.col)) : 0);
  const maxCol = $derived(bust.cells.length ? Math.max(...bust.cells.map((c) => c.col)) : 2);
  const cols = $derived(maxCol - minCol + 1);

  // A square crop centres on the figure's HEAD. Geometry alone misses it (the head can
  // sit off the occupied-column midpoint, e.g. Kaiba's duel disk widens one side), so we
  // use the pixel-derived `headAnchorX` baked into the manifest at decode time.
  const headCx = $derived(bust.headAnchorX ?? CANVAS / 2);

  // Full bust: scale the whole 768 canvas to `height`. Square crop: scale the slice
  // to fill, then centre the head region in the box.
  const scale = $derived(square ? height / sliceH : height / CANVAS);
  // Box dimensions + the offset that maps the crop region to the box centre.
  const boxW = $derived(square ? height : cols * CELL * scale);
  const boxH = $derived(square ? height : CANVAS * scale);
  const offX = $derived(square ? height / 2 - headCx * scale : -minCol * CELL * scale);
  const offY = $derived(square ? height / 2 - cy * scale : 0);
</script>

<div
  class="relative shrink-0 overflow-hidden"
  style="width:{boxW}px; height:{boxH}px"
  aria-hidden="true"
>
  {#each bust.cells as cell (cell.src)}
    <img
      src={asset(cell.src)}
      alt=""
      draggable="false"
      class="pointer-events-none absolute select-none"
      style="left:{cell.col * CELL * scale + offX}px; top:{cell.row * CELL * scale + offY}px;
             width:{CELL * scale}px; height:{CELL * scale}px"
    />
  {/each}
</div>
